import Vue from 'vue'
import axios from 'axios'
import { meta } from '@App/Meta'
import { formatarISO } from '@Shared/Date'
import * as AlertaSonoro from '@Shared/AlertaSonoro'
import * as AlertaNavegador from '@Shared/AlertaNavegador'
import { roomPassAgainstFilters } from '../../app/Store/Chat/Inbox'

export function findRoomById(state, id, opts = {}) {
    // Caso especial para a sala do assistente pessoal que msotramos na tela do operador.
    if (state.personalAssistantRoom) {
        if (state.personalAssistantRoom.id == id) return state.personalAssistantRoom
    }

    let tabs = Object.keys(state.salasByTab)
    for (let i = 0; i < tabs.length; i++) {
        if (opts.ignoreCache && tabs[i] == 'cache') continue
        if (state.salasByTab[tabs[i]] === null) continue

        let rooms = state.salasByTab[tabs[i]]
        let sala = rooms.find(s => s.id == id)
        if (sala) return sala
    }

    if (state.helpdeskPaginatedRooms && state.helpdeskPaginatedRooms.data) {
        return state.helpdeskPaginatedRooms.data.find(r => r.id == id)
    }
}

export function buildChatStore(attrs) {
    let { fastlane, aguardarPerfil } = attrs

    return {
        state: {
            /**
             * Quantos eventos devem ser mantidos em memória por sala?
             *
             * Durante uma sessão de uso da plataforma, o usuário irá navegar por várias salas de conversa. Para controlar o uso de memória e cache
             * local, nós devemos manter somente as últimas X mensagens na memória. Caso uma nova mensagem seja recebida, a mais antiga deve ser esquecida
             * para dar espaço para o novo evento.
             */
            quantidadeDeEventosEmMemóriaPorSala: 35,

            /**
             * Salas de conversa que o usuário tem aberto no momento.
             */
            salasByTab: {
                teams: null,
                chat: [],
                email: [],
                cache: [],
            },

            totalRoomsByTab: {
                teams: 0,
                chat: 0,
                email: 0,
            },

            /**
             * Mapa de eventos por sala.
             */
            eventosPorSala: {},

            /**
             * Registos de eventos não-lidos.
             */
            eventosNãoLidos: [],

            /**
             * Sala que o usuário está selecionada no momento.
             */
            salaSelecionada: null,

            /**
             * Quantidade de eventos que o usuário ainda não visualizou por estão fora da área de scroll. Se o usuário está na página da sala,
             * o evento foi tecnicamente marcado como lido. Esse dado serve apenas para controlar um overlay que mostramos com "x novas mensagens".
             */
            novosEventosForaDoScrollPorSala: {},

            /**
             * Nós precisamos desse lado pelo seguinte caso:
             *
             * O problema acontece quando a conexão do usuário sofre alguma lentidão no processo de enviar uma mensagem.
             * O evento de envio é recebido pelo servidor, que faz broadcast para as outras pessoas da sala. Se a internet dessas outras
             * pessoas estiverem rápidas, é possível que o autor da mensagem receba a confirmação de leitura *antes* de finalizar o
             * processo de envio do evento. Nesse caso, o autor recebe a confirmação de leitura de um evento que ele/ela nem sabe que existe.
             *
             * Se isso acontecer, nós armazenamos o ack nessa lista e quando o envio for confirmado pelo servidor, no futuro, nós temos o dado
             * do ack para atualizar.
             */
            acksDeEventosNãoPersistidos: [],
        },
        getters: {
            findRoomById: (state, getters) => id => {
                let sala = findRoomById(state, id)
                if (sala) return sala

                // Caso especial para a sala do assistente pessoal.
                if (state.personalAssistantRoom && state.personalAssistantRoom.id == id) {
                    return state.personalAssistantRoom
                }
            },

            tabTotal: state => tab => {
                return state.totalRoomsByTab[tab]
            },

            idSalaSelecionada: state => {
                let salaId = state.salaSelecionada

                if (!salaId) return null

                if (salaId.id) {
                    salaId = salaId.id
                }

                return salaId
            },

            salaSelecionada: (state, getters) => {
                let salaId = getters.idSalaSelecionada
                if (!salaId) return
                return getters.findRoomById(salaId)
            },

            /**
             * Lista de eventos da sala, já ordenados do mais antigo para o mais recente.
             */
            eventosDaSala: state => id_sala => {
                return state.eventosPorSala[id_sala] || { loading: true }
            },

            últimoEventoDaSala: (_state, getters) => id_sala => {
                let eventos = getters.eventosDaSala(id_sala)
                if (eventos && eventos.length > 0) return eventos[eventos.length - 1]

                let sala = getters.findRoomById(id_sala)
                if (sala) return sala.último_evento || {}
                return {}
            },

            canaisDeBroadcast: (state, getters) => room_id => {
                let sala = getters.findRoomById(room_id)
                if (!sala) return []

                let usuários = (sala.atributos_extras.users_ids || [])
                    .filter(id => {
                        let userId = meta('user-id')
                        if (userId) return id != meta('user-id')
                        return true
                    })
                    .map(id => 'core_users:' + id)

                let contatos = (sala.atributos_extras.contacts_ids || [])
                    .filter(id => {
                        let contato = state.contato
                        if (contato) return id != contato.id
                        return true
                    })
                    .map(id => 'crm_contacts:' + id)

                // Canais de broadcast dos contatos que foram mesclados. Isso garante que o evento será
                // emitido para o canal do contato antigo e para o novo.
                let merge = sala.atributos_extras.merge_broadcast_channels || []

                return usuários.concat(contatos).concat(merge)
            },

            /**
             * Quantidade de eventos não lidos, total de todas as salas.
             */
            countUnreadEvents: state => {
                let salas = {}

                state.eventosNãoLidos.forEach(({ evento }) => {
                    salas[evento.mensageiro_id] = true
                })

                return Object.keys(salas).length
            },

            /**
             * Total de mensagens não lidas.
             */
            countAllUnreadEvents: state => {
                return state.eventosNãoLidos.length
            },

            /**
             * Lista de eventos não lidos de uma sala do chat.
             */
            eventosNãoLidosDaSala: state => id_sala => {
                return state.eventosNãoLidos.filter(evento => {
                    return evento.evento.mensageiro_type == 'chat_salas' && evento.evento.mensageiro_id == id_sala
                })
            },

            /**
             * Quantos eventos estão fora do scroll que o usuário ainda não visualizou?
             */
            quantidadeDeNovosEventosForaDoScroll: state => id_sala => {
                return state.novosEventosForaDoScrollPorSala[id_sala]
            },

            totalRooms: state => tab => {
                return state.totalRoomsByTab[tab]
            },
        },
        mutations: {
            /**
             * Quantos eventos devem ser mantidos em memória por sala?
             *
             * Durante uma sessão de uso da plataforma, o usuário irá navegar por várias salas de conversa. Para controlar o uso de memória e cache
             * local, nós devemos manter somente as últimas X mensagens na memória. Caso uma nova mensagem seja recebida, a mais antiga deve ser esquecida
             * para dar espaço para o novo evento.
             */
            atualizarQuantidadeDeEventosEmMemóriaPorSala(state, quantidade) {
                state.quantidadeDeEventosEmMemóriaPorSala = quantidade
            },

            updateTabTotal(state, { tab, total }) {
                state.totalRoomsByTab[tab] = total
            },

            incTabTotal(state, tab) {
                state.totalRoomsByTab[tab] += 1
            },

            decTabTotal(state, tab) {
                state.totalRoomsByTab[tab] = Math.max(state.totalRoomsByTab[tab] - 1, 0)
            },

            pushRoomToTab(state, { tab, room }) {
                state.salasByTab[tab].push(room)
            },

            updateChatRoom(state, updatedRoom) {
                Object.keys(state.salasByTab).forEach(tabName => {
                    if (!Array.isArray(state.salasByTab[tabName])) return

                    state.salasByTab[tabName] = state.salasByTab[tabName].map(room => {
                        // Correção da sincronização de salas que ainda não foram abertas pela UI ou acabaram de ser recebidas
                        // por convite. Nesses casos, o último evento é obtido a partir do próprio objeto da sala, e o evento
                        // para atualizar a sala pode vir de diversos lugares (ex: marcou como importante e sincronizou por websocket).
                        // Nesses casos, o objeto que chega por websocket não contém o último evento, e nós devemos manter para que
                        // a ordem na lista de atendimentos, seja no chat ou no helpdesk, seja mantida.
                        if (updatedRoom.id == room.id && room.último_evento && !updatedRoom.último_evento) {
                            updatedRoom.último_evento = room.último_evento
                        }

                        return updatedRoom.id == room.id ? updatedRoom : room
                    })
                })

                // Caso especial para receber atualização do registro da sala que está armazenada na paginação da Inbox.
                if (state.helpdeskPaginatedRooms && state.helpdeskPaginatedRooms.data) {
                    state.helpdeskPaginatedRooms.data = state.helpdeskPaginatedRooms.data.map(room => {
                        return room.id == updatedRoom.id ? updatedRoom : room
                    })
                }
            },

            updateEvent(state, event) {
                let eventosAtuais = state.eventosPorSala[event.mensageiro_id]
                if (eventosAtuais) {
                    state.eventosPorSala[event.mensageiro_id] = eventosAtuais.map(e => {
                        if (e.id == event.id) return { ...e, atributos_extras: event.atributos_extras }
                        return e
                    })

                    document.dispatchEvent(
                        new CustomEvent('chat:scroll-to-bottom', { detail: { id_sala: event.mensageiro_id } })
                    )
                }
            },

            /**
             * Remove uma sala de conversa da UI.
             */
            async deleteChatRoom(state, id) {
                let room = findRoomById(state, id)

                Object.keys(state.salasByTab).forEach(tabName => {
                    if (!Array.isArray(state.salasByTab[tabName])) return

                    state.salasByTab[tabName] = state.salasByTab[tabName].filter(room => {
                        return room.id != id
                    })
                })

                if (state.helpdeskPaginatedRooms && state.helpdeskPaginatedRooms.data) {
                    let foundInList = false

                    state.helpdeskPaginatedRooms.data = state.helpdeskPaginatedRooms.data.filter(r => {
                        if (r.id == id) {
                            foundInList = true
                            return false
                        }

                        return true
                    })

                    if (foundInList) {
                        console.log('chat:room-removed-from-inbox', new Date())
                        document.dispatchEvent(new CustomEvent('chat:room-removed-from-inbox'))
                    }

                    if (window.cockpitHelpdeskFilters) {
                        let filters = window.cockpitHelpdeskFilters()
                        if (Array.isArray(filters)) {
                            filters.forEach(filter => {
                                if (
                                    roomPassAgainstFilters(
                                        room,
                                        filter.configuração.filtros,
                                        state.helpdeskCustomFilters
                                    )
                                ) {
                                    filter.total = Math.max(filter.total - 1, 0)
                                }
                            })
                        }
                    }
                }

                Vue.delete(state.eventosPorSala, id)

                document.dispatchEvent(new CustomEvent('chat:room-deleted-from-ui', { detail: { id } }))
            },

            /**
             * O nome da função é "mesclar" justamente para descrever que essa é uma ação não-destrutiva. A ideia é poder chamar
             * essa função sem medo de perder a orderm dos eventos que já estão na memória.
             */
            mesclarEventosDaSala(state, { append, id_sala, eventos, referência }) {
                let eventosAtuais = state.eventosPorSala[id_sala]
                if (!eventosAtuais) {
                    eventosAtuais = Vue.set(state.eventosPorSala, id_sala, [])
                }

                eventos.forEach(evento => {
                    // Impedir eventos duplicados
                    if (eventosAtuais.find(e => evento.id && e.id == evento.id)) return

                    if (append) {
                        eventosAtuais.push(evento)
                        return
                    }

                    // O evento que contém "initial_description" nos atributos extras é usado para descrever o perfil do contato
                    // e parâmetros de abertura. Essa mensagem deve ser sempre a primeira a ser mostrada.
                    let insertIndex = eventosAtuais.findIndex(
                        e => evento.created_at < e.created_at && !e.atributos_extras.initial_description
                    )

                    if (insertIndex !== -1) {
                        eventosAtuais.splice(insertIndex, 0, evento)
                    } else {
                        eventosAtuais.push(evento)
                    }
                })

                // Se os eventos foram carregados a partir de uma referência e a quantidade é menor do que
                // o chunk carregado, nós devemos marcar uma flag 'end_of_history' para esconder o botão de carregar
                // mais antigos da interface.
                let chunkAmount = 35
                if (referência && eventos.length < chunkAmount) {
                    Vue.set(eventosAtuais[0], 'end_of_history', true)
                }

                // if (!referência && eventosAtuais.length > state.quantidadeDeEventosEmMemóriaPorSala) {
                //     eventosAtuais.splice(0, eventosAtuais.length - state.quantidadeDeEventosEmMemóriaPorSala)
                // }
            },

            /**
             * Quando o usuário manda uma mensagem (na tela de uma conversa, digita algo no texto e aperta enter), nós adicionamos o evento
             * imediatamente (por uma questão de UX) e então enviamos o evento por websocket. Nesse evento do websocket o servidor retorna
             * com a confirmação do evento, já com o ID. Nesse ponto nós precisamos substituir o evento temporário pelo final.
             */
            confirmarEnvioDeEvento(state, { id_sala, evento, id_temporário }) {
                state.eventosPorSala[id_sala] = state.eventosPorSala[id_sala].map(eventoAtual => {
                    if (eventoAtual.id_temporário == id_temporário) return evento
                    return eventoAtual
                })
            },

            /**
             * Confirmação de recebimento ou leitura de mensagens que o usuário atual enviou. Canal representa a pessoa, como "core_users:10" ou "crm_contacts:10".
             * Ack representa 1 - recebimento, 2 - leitura.
             *
             * Antes de atualizar o ack nós verificamos se o valor atual é menor do que o novo. Caso não seja, nós não devemos atualizar.
             * Isso pode acontecer em casos onde, por causa de instabilidade ou lentidão na rede, a confirmação de recebimento chega depois da confirmação de leitura.
             */
            atualizarAck(state, { canal, evento: eventData, ack }) {
                if (!eventData) return

                let { id, mensageiro_type, mensageiro_id } = eventData

                let eventos = state.eventosPorSala[mensageiro_id] || []
                let evento = eventos.find(evento => evento.id == id)

                if (evento) {
                    if (evento.atributos_extras.acks && evento.atributos_extras.acks[canal]) {
                        if (ack > evento.atributos_extras.acks[canal]) {
                            evento.atributos_extras.acks[canal] = ack
                        }
                    } else {
                        Vue.set(evento.atributos_extras.acks, canal, ack)
                    }
                } else {
                    // Esse problema acontece quando a conexão do usuário sofre alguma lentidão no processo de enviar uma mensagem.
                    // O evento de envio é recebido pelo servidor, que faz broadcast para as outras pessoas da sala. Se a internet dessas outras
                    // pessoas estiverem rápidas, é possível que o autor da mensagem receba a confirmação de leitura *antes* de finalizar o
                    // processo de envio do evento. Nesse caso, o autor recebe a confirmação de leitura de um evento que ele/ela nem sabe que existe.
                    state.acksDeEventosNãoPersistidos.push({
                        canal,
                        evento: { id, mensageiro_type, mensageiro_id },
                        ack,
                    })
                }
            },

            /**
             * Houve falha no envio de mensagem enviada para um canal externo, como whatsapp ou e-mail.
             */
            atualizarFalhaDeEnvio(state, { evento: eventoQueFalhou, error }) {
                let eventos = state.eventosPorSala[eventoQueFalhou.mensageiro_id] || []
                let evento = eventos.find(evento => evento.id == eventoQueFalhou.id)

                if (evento) {
                    Vue.set(evento.atributos_extras, 'error', error)
                }
            },

            incrementarNovosEventosForaDoScroll(state, { id_sala }) {
                if (state.novosEventosForaDoScrollPorSala[id_sala]) {
                    state.novosEventosForaDoScrollPorSala[id_sala] += 1
                } else {
                    Vue.set(state.novosEventosForaDoScrollPorSala, id_sala, 1)
                }
            },

            resetarNovosEventosForaDoScroll(state, { id_sala }) {
                Vue.delete(state.novosEventosForaDoScrollPorSala, id_sala)
            },

            /**
             * Adiciona o evento na lista de eventos não lidos.
             */
            adicionarEventoNãoLido(state, registro_não_lido) {
                state.eventosNãoLidos.push(registro_não_lido)
            },

            limparEventoNãoLido(state, registro_não_lido) {
                state.eventosNãoLidos = state.eventosNãoLidos.filter(r => {
                    return r.evento.id != registro_não_lido.evento.id
                })
            },

            limparEventosNãoLidosDaSala(state, roomId) {
                state.eventosNãoLidos = state.eventosNãoLidos.filter(r => {
                    return r.evento.mensageiro_id != roomId
                })
            },
        },
        actions: {
            /**
             * Carrega os eventos não-lidos do usuário.
             */
            async carregarEventosNãoLidos({ commit, dispatch, getters }) {
                let { registros_não_lidos } = await fastlane('chat:carregar_eventos_não_lidos', {})

                registros_não_lidos.forEach(registro => {
                    // Pode ser que tenha um registro não lido de uma sala que nós não temos mais na interface.
                    // Nós devemos ignorar. A exceção dessa regra é a sala do assistente pessoal, que é carregado
                    // de forma independente e nós não podemos ignorar o evento não lido.
                    if (
                        getters.findRoomById(registro.evento.mensageiro_id) ||
                        registro.mensageiro_canal == 'personal_assistant'
                    ) {
                        commit('adicionarEventoNãoLido', registro)
                    }
                })

                dispatch('talvezMarcarEventosDaSalaSelecionadaComoLidos')
            },

            /**
             * Remove uma sala de conversa da UI e decrementar o total.
             */
            deleteChatRoom({ commit }, sala) {
                commit('deleteChatRoom', sala.id)
                commit('decTabTotal', roomTabName(sala))
            },

            /**
             * Envia uma mensagem para a sala com feedback imediato na interface
             */
            async enviarMensagem({ commit, getters }, dadosDoEnvio) {
                let id_temporário = (Math.random() + 1).toString(36).substring(7) + new Date().getTime()

                let sala = getters.findRoomById(dadosDoEnvio.mensageiro_id) || dadosDoEnvio.room
                if (!sala) {
                    window.alert(__('Não foi possível enviar a mensagem. Por favor, tente novamente mais tarde.'))
                    return
                }

                let extra = dadosDoEnvio.atributos_extras || {}

                let evento = {
                    id_temporário,
                    id_cliente: dadosDoEnvio.id_cliente || meta('cliente-id'),
                    mensageiro_type: dadosDoEnvio.mensageiro_type,
                    mensageiro_id: dadosDoEnvio.mensageiro_id,
                    mensageiro_canal: sala.canal_atual,
                    mensageiro_nome: sala.atributos_extras.nome, // Somente existe em grupos
                    integration_id: dadosDoEnvio.integration_id || sala.integration_id,
                    integration_type: sala.integration_type,
                    visibility: dadosDoEnvio.visibility || 'public',
                    corpo_html: dadosDoEnvio.corpo_html,
                    corpo_texto: dadosDoEnvio.corpo_texto,
                    autor_type: dadosDoEnvio.autor_type || 'core_users',
                    autor_id: dadosDoEnvio.autor_id || meta('user-id'),
                    autor_avatar_url: meta('user-avatar-128px-url'),
                    autor_nome: meta('user-nome'),
                    broadcast: getters.canaisDeBroadcast(dadosDoEnvio.mensageiro_id),
                    // created_at: formatarISO(new Date()),
                    canonical_id: sala.id_canônico,
                    host: dadosDoEnvio.host || sala.atributos_extras.host,
                    atributos_extras: {
                        ...extra,
                        anexos: dadosDoEnvio.anexos,
                        bot_id: sala.atributos_extras.bot_id,
                    },
                }

                commit('mesclarEventosDaSala', {
                    append: true,
                    id_sala: dadosDoEnvio.mensageiro_id,
                    eventos: [{ ...evento, created_at: formatarISO(new Date()) }],
                })

                document.dispatchEvent(
                    new CustomEvent('chat:scroll-to-bottom', {
                        detail: { id_sala: dadosDoEnvio.mensageiro_id },
                    })
                )

                fastlane('chat:enviar_mensagem', evento, { retry: true })
            },

            /**
             * Confirmação que o evento foi enviado e persistido com sucesso. O id_temporário irá existir se a sessão que enviou
             * a mensagem for a mesma que está aberta.
             */
            async confirmarEnvioDeMensagem({ commit, getters, state }, { evento, id_temporário }) {
                let eventos = getters.eventosDaSala(evento.mensageiro_id)
                if (!eventos || eventos.loading) return

                let eventoTemporárioNessaSessão = eventos.find(e => e.id_temporário == id_temporário)

                if (eventoTemporárioNessaSessão) {
                    commit('confirmarEnvioDeEvento', {
                        id_sala: evento.mensageiro_id,
                        evento: evento,
                        id_temporário,
                    })
                } else {
                    commit('mesclarEventosDaSala', { id_sala: evento.mensageiro_id, eventos: [evento] })
                }

                let ack = state.acksDeEventosNãoPersistidos.find(e => e.evento.id == evento.id)
                if (ack) {
                    state.acksDeEventosNãoPersistidos = state.acksDeEventosNãoPersistidos.filter(a => a != ack)
                    commit('atualizarAck', ack)
                }
            },

            /**
             * Carrega os últimos eventos da sala.
             */
            async carregarEventosDaSala(
                { commit },
                { id_cliente, room_token, id_sala, referência, channel, retryIfEmpty }
            ) {
                if (!id_cliente) id_cliente = meta('cliente-id')
                if (!id_cliente) return

                let dados = {
                    channel,
                    user_id: meta('user-id'),
                    user_token: meta('user-token'),
                    id_cliente: id_cliente.toString(),
                    room_token: room_token,
                    mensageiro_type: 'chat_salas',
                    mensageiro_id: id_sala.toString(),
                }

                if (referência) dados.referência = referência

                let attempts = 0
                const getEvents = () => {
                    axios
                        .get(meta('fastlane-api') + '/api/chat/events_loader', {
                            params: dados,
                        })
                        .then(res => {
                            if (!res.data.eventos.length) {
                                if (retryIfEmpty && attempts == 0) {
                                    setTimeout(() => {
                                        attempts += 1
                                        getEvents()
                                    }, 1000)
                                }

                                // Mesmo sem retornar nada, nós precisamos mesclar os eventos da sala
                                // para que o 'end_of_history" seja atualizado para o cliente saber que
                                // não há nada mais para recarregar.
                                commit('mesclarEventosDaSala', {
                                    id_sala: id_sala,
                                    eventos: res.data.eventos,
                                    referência,
                                })

                                return
                            }

                            commit('mesclarEventosDaSala', {
                                id_sala: id_sala,
                                eventos: res.data.eventos,
                                referência,
                            })

                            if (!referência) {
                                document.dispatchEvent(
                                    new CustomEvent('chat:scroll-to-bottom', { detail: { id_sala, force: true } })
                                )
                            }
                        })
                        .catch(err => {
                            console.log(err)

                            window.alert(
                                __('Não foi possível carregar as mensagens. Por favor, tente novamente mais tarde.')
                            )
                        })
                }

                getEvents()
            },

            /**
             * Novo evento recebido por websocket de outra pessoa. Nesse caso, nós precisamos rodar as seguintes ações:
             *
             * - adicionar o evento a lista de eventos na memória
             * - se o chamado do evento estiver selecionado
             *   - se a janela estiver focada
             *     - sim: marcar o evento como lida
             *     - não: marcar o evento como recebido e incrementar eventos não lidos
             *   - se não, marcar o evento como recebido e incrementar eventos não lidos
             */
            async novoEventoRecebido({ commit, dispatch, state, rootState, getters }, { evento, registro_não_lido }) {
                if (evento.mensageiro_type == 'chat_salas') {
                    let id_sala = evento.mensageiro_id

                    let sala = getters.findRoomById(id_sala)

                    if (!state.eventosPorSala[id_sala] || state.eventosPorSala[id_sala].length == 0) {
                        dispatch('carregarEventosDaSala', { id_sala, room_token: sala && sala.token })
                    }

                    // Se por acaso a sala estava em fila e uma mensagem foi recebida, quer dzier que nós perdemos
                    // um evento e devemos forçar o estado para 'open'.
                    if (evento.autor_type == 'core_users' && sala && sala.status == 'queued') {
                        Vue.set(sala, 'status', 'open')
                    }

                    // Se nós acabamos de receber um evento de uma sala que não conhecemos ainda, precisamos carregar o registro.
                    // Isso geralmente quer dizer uma das duas opções:
                    // 1. O usuário foi adicionado em um grupo depois de ter iniciado a sessão e alguém mandou uma mensagem no grupo.
                    // 2. O usuário recebeu uma mensagem em um atendimento antigo que ainda não foi finalizado, mas está fora da interface e não foi carregado no primeiro load.
                    if (!sala && getters.encontrarConvitePorId && !getters.encontrarConvitePorId(id_sala)) {
                        dispatch('carregarSala', { id_sala, emitir_alerta_após_carregar: { evento } })
                    }

                    let flash = evento.autor_type + ':' + evento.autor_id

                    commit('flash/removerFlash', { chave: 'chat_salas:' + id_sala, valor_id: flash }, { root: true })

                    commit('mesclarEventosDaSala', { id_sala, eventos: [evento] })

                    document.dispatchEvent(new CustomEvent('chat:scroll-to-bottom', { detail: { id_sala } }))

                    if (registro_não_lido) {
                        if (getters.idSalaSelecionada == id_sala) {
                            if (rootState.focoJanela) {
                                await markRoomEventsAsRead(fastlane, sala)
                            } else {
                                await fastlane('chat:confirmar_recebimento', evento)
                                commit('adicionarEventoNãoLido', registro_não_lido)
                            }
                        } else {
                            if (!sala || sala.status == 'queued') {
                                // Não devemos adicionar o evento
                            } else {
                                await fastlane('chat:confirmar_recebimento', evento)
                                commit('adicionarEventoNãoLido', registro_não_lido)
                            }
                        }

                        dispatch('talvezEmitirAlertasParaNovoEvento', { id_sala, evento })
                    }
                }
            },

            async talvezMarcarEventosDaSalaSelecionadaComoLidos({ getters, dispatch }) {
                if (!getters.salaSelecionada) return
                dispatch('markRoomEventsAsRead', getters.salaSelecionada)
            },

            async markRoomEventsAsRead({ commit, getters }, room) {
                let eventos = getters.eventosNãoLidosDaSala(room.id).filter(registro_não_lido => {
                    commit('limparEventoNãoLido', registro_não_lido)
                    return true
                })

                let isEmail = room.canal_atual && canaisDoEmail.indexOf(room.canal_atual) !== -1
                let chatUnreadEmpty = !eventos || eventos.length === 0
                if (!isEmail && chatUnreadEmpty) return

                await markRoomEventsAsRead(fastlane, room)
            },

            async talvezEmitirAlertasParaNovoEvento({ rootState, getters }, { id_sala, evento }) {
                if (!rootState.focoJanela) {
                    if (getters.notificaçõesHabilitadas(id_sala) === true) {
                        AlertaSonoro.play('/sound/intuition.ogg')

                        if (!aguardarPerfil) return

                        // Para mostrar a notificação nós precisamos aguardar o perfil estar disponível. Se já estiver em memória,
                        // a promise é resolvida imediatamente.
                        let canalDoAutor = evento.autor_type + ':' + evento.autor_id
                        let perfil = await aguardarPerfil(canalDoAutor)

                        if (!perfil) return

                        AlertaNavegador.notificar({
                            title: perfil.name,
                            body: evento.corpo_texto,
                            icon: perfil.url_avatar_32px,
                            url: '/chat/rooms/' + id_sala,
                        })

                        window.AlertaNavegador = AlertaNavegador
                    }
                }
            },
        },
    }
}

/**
 * Atualiza os eventos da sala como lidos.
 *
 * @param {*} room
 */
export async function markRoomEventsAsRead(fastlane, room) {
    if (room && room.canal_atual && canaisDoEmail.indexOf(room.canal_atual) !== -1) {
        axios.delete('/chat/private_api/unread_counter/' + room.id)
    } else {
        await fastlane('chat:confirmar_leitura', { sala: room })
    }
}

/**
 * Lista exaustiva com os códigos dos canais do teams.
 */
export const canaisDoTeams = ['direct_message', 'group', 'personal_assistant']

/**
 * Lista exaustiva com os canais do chat.
 */
export const canaisDoChat = [
    'web_messenger',
    'whatsapp_qr_code',
    'whatsapp_twilio',
    'whatsapp_remote',
    'instagram',
    'telegram',
    'messenger',
]

/**
 * Lista exaustiva dos canais por e-mail.
 */
export const canaisDoEmail = ['email', 'web_form', 'telefone']

export function roomTabName(sala) {
    if (canaisDoTeams.indexOf(sala.canal_atual) !== -1) return 'teams'
    if (canaisDoChat.indexOf(sala.canal_atual) !== -1) return 'chat'
    if (canaisDoEmail.indexOf(sala.canal_atual) !== -1) return 'email'
}
